import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  TextInput,
  Pressable,
  StyleSheet,
  ActivityIndicator,
  Keyboard,
  Platform,
} from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';
import { redeemPricingCode } from '../../services/pricing-code-service';
import { isSuccessful } from '../../services/api-requests';

interface RedeemCodeInputProps {
  chargerId: string;
  onCodeRedeemed: () => void;
}

export default function RedeemCodeInput({ chargerId, onCodeRedeemed }: RedeemCodeInputProps) {
  const [expanded, setExpanded] = useState(false);
  const [code, setCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [feedback, setFeedback] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [keyboardHeight, setKeyboardHeight] = useState(0);

  useEffect(() => {
    if (Platform.OS === 'web') return;

    const showEvent = Platform.OS === 'ios' ? 'keyboardWillShow' : 'keyboardDidShow';
    const hideEvent = Platform.OS === 'ios' ? 'keyboardWillHide' : 'keyboardDidHide';

    const showSub = Keyboard.addListener(showEvent, (e) => {
      setKeyboardHeight(e.endCoordinates.height);
    });
    const hideSub = Keyboard.addListener(hideEvent, () => {
      setKeyboardHeight(0);
    });

    return () => {
      showSub.remove();
      hideSub.remove();
    };
  }, []);

  const handleSubmit = async () => {
    const trimmed = code.trim();
    if (!trimmed) return;

    setSubmitting(true);
    setFeedback(null);
    try {
      const response = await redeemPricingCode(trimmed, chargerId);
      if (isSuccessful(response.status)) {
        setFeedback({ type: 'success', message: response.payload?.message ?? 'Code applied!' });
        setCode('');
        onCodeRedeemed();
      } else {
        setFeedback({ type: 'error', message: 'Invalid or expired code.' });
      }
    } catch {
      setFeedback({ type: 'error', message: 'Something went wrong. Try again.' });
    } finally {
      setSubmitting(false);
    }
  };

  if (!expanded) {
    return (
      <Pressable
        style={({ pressed }) => [styles.collapsedRow, { opacity: pressed ? 0.7 : 1 }]}
        onPress={() => setExpanded(true)}
      >
        <MaterialIcons name="confirmation-number" size={18} color="#8D8E90" />
        <Text style={styles.collapsedText}>Have an access code?</Text>
        <MaterialIcons name="chevron-right" size={20} color="#8D8E90" />
      </Pressable>
    );
  }

  return (
    <View style={styles.container}>
      <Pressable
        style={styles.headerRow}
        onPress={() => {
          setExpanded(false);
          setFeedback(null);
        }}
      >
        <MaterialIcons name="confirmation-number" size={18} color="#40BFB4" />
        <Text style={styles.headerText}>Enter Access Code</Text>
        <MaterialIcons name="keyboard-arrow-up" size={22} color="#8D8E90" />
      </Pressable>

      <View style={styles.inputRow}>
        <TextInput
          style={styles.input}
          placeholder="Code"
          placeholderTextColor="#8D8E90"
          value={code}
          onChangeText={(text) => {
            setCode(text);
            if (feedback) setFeedback(null);
          }}
          autoCapitalize="characters"
          autoCorrect={false}
          editable={!submitting}
        />
        <Pressable
          style={({ pressed }) => [
            styles.applyButton,
            (!code.trim() || submitting) && styles.applyButtonDisabled,
            { opacity: pressed && code.trim() && !submitting ? 0.8 : 1 },
          ]}
          onPress={handleSubmit}
          disabled={!code.trim() || submitting}
        >
          {submitting ? (
            <ActivityIndicator size="small" color="#FFFFFF" />
          ) : (
            <Text style={styles.applyText}>Apply</Text>
          )}
        </Pressable>
      </View>

      {feedback && (
        <View style={styles.feedbackRow}>
          <MaterialIcons
            name={feedback.type === 'success' ? 'check-circle' : 'error'}
            size={16}
            color={feedback.type === 'success' ? '#10B981' : '#EF4444'}
          />
          <Text
            style={[
              styles.feedbackText,
              { color: feedback.type === 'success' ? '#10B981' : '#EF4444' },
            ]}
          >
            {feedback.message}
          </Text>
        </View>
      )}

      {keyboardHeight > 0 && (
        <View style={{ height: keyboardHeight }} />
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  collapsedRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 12,
    gap: 8,
  },
  collapsedText: {
    color: '#8D8E90',
    fontSize: 14,
    fontFamily: 'PlusJakartaSans-Regular',
    flex: 1,
  },
  container: {
    backgroundColor: '#292D33',
    borderRadius: 10,
    marginHorizontal: 16,
    marginTop: 10,
    padding: 14,
  },
  headerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginBottom: 12,
  },
  headerText: {
    color: '#FFFFFF',
    fontSize: 14,
    fontFamily: 'PlusJakartaSans-Regular',
    fontWeight: '600',
    flex: 1,
  },
  inputRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
  },
  input: {
    flex: 1,
    backgroundColor: '#1A1D21',
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 10,
    color: '#FFFFFF',
    fontSize: 14,
    fontFamily: 'PlusJakartaSans-Regular',
    borderWidth: 1,
    borderColor: '#3A3D42',
  },
  applyButton: {
    backgroundColor: '#40BFB4',
    borderRadius: 8,
    paddingHorizontal: 18,
    paddingVertical: 10,
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: 70,
  },
  applyButtonDisabled: {
    backgroundColor: '#3A3D42',
  },
  applyText: {
    color: '#FFFFFF',
    fontSize: 14,
    fontFamily: 'PlusJakartaSans-Regular',
    fontWeight: '700',
  },
  feedbackRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    marginTop: 10,
  },
  feedbackText: {
    fontSize: 13,
    fontFamily: 'PlusJakartaSans-Regular',
  },
});
